import { Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormMoneyField from '../../../FormFields/FormMoneyField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { formCardStyle } from '../../../TransitionStyles';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { TELECOMMUNICATIONS } from '../../../formQuestionsEnum';

InternetForm.propTypes = {
  permissions: PropTypes.object,
};

const fieldIds = [
  TELECOMMUNICATIONS.INTERNET_PROVIDER.id,
  TELECOMMUNICATIONS.INTERNET_MONTHLY_COST.id,
  TELECOMMUNICATIONS.IS_INTERNET_EQUIPMENT_LEASED.id,
  TELECOMMUNICATIONS.INTERNET_LEASE_MONTHLY_COST.id,
  TELECOMMUNICATIONS.INTERNET_CIRCUIT_TYPE.id,
  TELECOMMUNICATIONS.INTERNET_BANDWIDTH.id,
  TELECOMMUNICATIONS.INTERNET_BACKUP_CIRCUIT_TYPE.id,
  TELECOMMUNICATIONS.INTERNET_BACKUP_BANDWIDTH.id,
];

export default function InternetForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const showForm = fieldIds.some((id) => id in permissions);

  const { watch } = useFormContext();
  const equipmentLeased = watch(TELECOMMUNICATIONS.IS_INTERNET_EQUIPMENT_LEASED.fieldName);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Internet
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {TELECOMMUNICATIONS.INTERNET_PROVIDER.id in permissions && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Internet provider', fieldType: 'title' }} />
                <FormVendorField
                  fieldName={TELECOMMUNICATIONS.INTERNET_PROVIDER.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.INTERNET_PROVIDER.id] }}
                  invoiceFileTypeId={FILE_TYPES.INTERNET_SERVICE_PROVIDER_INVOICE}
                  contractFileTypeId={FILE_TYPES.INTERNET_SERVICE_PROVIDER_CONTRACT}
                />
              </FieldContainer>
            </Grid>
          )}
          {TELECOMMUNICATIONS.INTERNET_MONTHLY_COST.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Current monthly cost' }} />
              <FormMoneyField
                fieldName={TELECOMMUNICATIONS.INTERNET_MONTHLY_COST.fieldName}
                fieldAttributes={{
                  isEditable: permissions[TELECOMMUNICATIONS.INTERNET_MONTHLY_COST.id],
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {TELECOMMUNICATIONS.IS_INTERNET_EQUIPMENT_LEASED.id in permissions && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Equipment' }} />
                <FormButtonOptions
                  fieldName={TELECOMMUNICATIONS.IS_INTERNET_EQUIPMENT_LEASED.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[TELECOMMUNICATIONS.IS_INTERNET_EQUIPMENT_LEASED.id],
                    options: [
                      { label: 'Owned', value: false },
                      { label: 'Leased', value: true },
                    ],
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {equipmentLeased && TELECOMMUNICATIONS.INTERNET_LEASE_MONTHLY_COST.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Monthly cost to lease' }} />
              <FormMoneyField
                fieldName={TELECOMMUNICATIONS.INTERNET_LEASE_MONTHLY_COST.fieldName}
                fieldAttributes={{
                  isEditable: permissions[TELECOMMUNICATIONS.INTERNET_LEASE_MONTHLY_COST.id],
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {TELECOMMUNICATIONS.INTERNET_CIRCUIT_TYPE.id in permissions && (
            <Grid item xs={12} lg={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Circuit type' }} />
                <FormTextField
                  fieldName={TELECOMMUNICATIONS.INTERNET_CIRCUIT_TYPE.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.INTERNET_CIRCUIT_TYPE.id] }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}
          {TELECOMMUNICATIONS.INTERNET_BANDWIDTH.id in permissions && (
            <Grid item xs={12} lg={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bandwidth' }} />
                <FormTextField
                  fieldName={TELECOMMUNICATIONS.INTERNET_BANDWIDTH.fieldName}
                  fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.INTERNET_BANDWIDTH.id] }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}
          {(TELECOMMUNICATIONS.INTERNET_BACKUP_CIRCUIT_TYPE.id in permissions ||
            TELECOMMUNICATIONS.INTERNET_BACKUP_BANDWIDTH.id in permissions) && (
            <>
              <Grid item xs={12}>
                <FormTitle fieldAttributes={{ questionTitle: 'Backup information', fieldType: 'title' }} />
              </Grid>
              {TELECOMMUNICATIONS.INTERNET_BACKUP_CIRCUIT_TYPE.id in permissions && (
                <Grid item xs={12} lg={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Circuit type' }} />
                    <FormTextField
                      fieldName={TELECOMMUNICATIONS.INTERNET_BACKUP_CIRCUIT_TYPE.fieldName}
                      fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.INTERNET_BACKUP_CIRCUIT_TYPE.id] }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}
              {TELECOMMUNICATIONS.INTERNET_BACKUP_BANDWIDTH.id in permissions && (
                <Grid item xs={12} lg={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Bandwidth' }} />
                    <FormTextField
                      fieldName={TELECOMMUNICATIONS.INTERNET_BACKUP_BANDWIDTH.fieldName}
                      fieldAttributes={{ isEditable: permissions[TELECOMMUNICATIONS.INTERNET_BACKUP_BANDWIDTH.id] }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Card>
    )
  );
}
